import { Controller } from 'stimulus';
import { Turbo } from '@hotwired/turbo-rails';
import { fileMatchesAccept } from '../util';

// Client Project Requests card domain behavior: cover image replace/delete with a
// live preview, enabling the questionnaire dropdown only when its radio is selected,
// and blocking submit until both a destination and a questionnaire are chosen. Dirty
// state is handled by the shared `dirty-form` controller.
export default class extends Controller {
  static targets = [
    'coverError',
    'coverInput',
    'coverPreview',
    'deleteButton',
    'destinationError',
    'enabledToggle',
    'removeCover',
    'questionnaireSelect',
    'sourceRadio',
  ];

  static values = {
    defaultCoverUrl: String,
    hasSavedCover: Boolean,
    previewUrl: String,
    maxFileSize: Number,
  };

  // Errors stay unpainted until the user actually tries to save, so a card they have
  // not submitted yet never looks broken.
  #submitAttempted = false;

  #overflowing = new Set();

  connect() {
    this.syncQuestionnaireState();
    // Also clears a stale error if Turbo restores a cached page mid-error, since
    // #submitAttempted starts false on every connect.
    this.#refreshValidation();
  }

  copyInvalid(event) {
    this.#overflowing.add(event.target);
    this.#syncBlocked();
  }

  copyValid(event) {
    this.#overflowing.delete(event.target);
    this.#syncBlocked();
  }

  // Open the client request-project preview in a modal, resolved server-side from the
  // card's current unsaved text values.
  async preview() {
    const url = new URL(this.previewUrlValue, window.location.origin);
    url.searchParams.set('request_project_enabled', this.enabledChecked ? '1' : '0');
    url.searchParams.set('request_project_title', this.fieldValue('request_project_title'));
    url.searchParams.set('request_project_subtitle', this.fieldValue('request_project_subtitle'));
    url.searchParams.set('request_project_cta_label', this.fieldValue('request_project_cta_label'));

    // The cover is a file upload, so it can't ride along as a URL param; resolve the
    // unsaved selection (or unsaved removal) here and swap it in once the modal mounts.
    const unsavedCoverUrl = this.unsavedCoverUrl();

    const response = await fetch(url, {
      headers: { Accept: 'text/vnd.turbo-stream.html' },
      credentials: 'same-origin',
    });

    // A redirect (session expired or flag off) yields HTML, not a stream; fall back to
    // a full navigation instead of feeding non-stream markup to Turbo.
    const contentType = response.headers.get('content-type') || '';
    if (response.redirected || !contentType.includes('turbo-stream')) {
      Turbo.visit(response.url);
      return;
    }

    // The modal is appended asynchronously by the open_dialog stream action, so swap the
    // cover once the modal connects. before-open bubbles from the ui--turbo-modal
    // controller. Registered only after a successful response so a failed request never
    // leaves a dangling listener.
    if (unsavedCoverUrl) {
      document.addEventListener(
        'ui--turbo-modal:before-open',
        (event) => {
          const banner = event.target.querySelector('.request-project-banner__cover');
          if (banner) {
            banner.style.backgroundImage = `url("${unsavedCoverUrl}")`;
          }
        },
        { once: true }
      );
    }

    Turbo.renderStreamMessage(await response.text());
  }

  unsavedCoverUrl() {
    if (this.coverObjectUrl) {
      return this.coverObjectUrl;
    }
    if (this.removeCoverTarget.value === '1') {
      return this.defaultCoverUrlValue;
    }
    return null;
  }

  get enabledChecked() {
    return this.hasEnabledToggleTarget && this.enabledToggleTarget.checked;
  }

  fieldValue(name) {
    return this.element.querySelector(`[name="${name}"]`)?.value ?? '';
  }

  disconnect() {
    this.revokeCoverObjectUrl();
  }

  browse() {
    this.coverInputTarget.click();
  }

  coverSelected() {
    const file = this.coverInputTarget.files[0];
    if (!file) {
      return;
    }
    if (!this.isValidCover(file)) {
      this.showCoverError();
      this.coverInputTarget.value = '';
      return;
    }

    this.clearCoverError();
    this.revokeCoverObjectUrl();
    this.coverObjectUrl = URL.createObjectURL(file);
    this.removeCoverTarget.value = '0';
    this.coverPreviewTarget.src = this.coverObjectUrl;
    this.deleteButtonTarget.classList.remove('hidden');
    this.markDirty();
  }

  isValidCover(file) {
    return (
      fileMatchesAccept(file, this.coverInputTarget.accept) && file.size <= this.maxFileSizeValue
    );
  }

  showCoverError() {
    this.coverPreviewTarget.classList.add('field--error');
    this.coverErrorTarget.classList.remove('hidden');
  }

  clearCoverError() {
    this.coverPreviewTarget.classList.remove('field--error');
    this.coverErrorTarget.classList.add('hidden');
  }

  removeCover() {
    this.revokeCoverObjectUrl();
    this.removeCoverTarget.value = this.hasSavedCoverValue ? '1' : '0';
    this.coverInputTarget.value = '';
    this.coverPreviewTarget.src = this.defaultCoverUrlValue;
    this.deleteButtonTarget.classList.add('hidden');
    this.clearCoverError();
    this.markDirty();
  }

  revokeCoverObjectUrl() {
    if (this.coverObjectUrl) {
      URL.revokeObjectURL(this.coverObjectUrl);
      this.coverObjectUrl = null;
    }
  }

  sourceChanged() {
    this.syncQuestionnaireState();
    this.#refreshValidation();
  }

  enabledChanged() {
    this.syncQuestionnaireState();
    this.#refreshValidation();
  }

  questionnaireChanged() {
    this.#refreshValidation();
  }

  // The radios are `display: none` and the select carries no native `required`, so the
  // browser cannot report on this card — and its bubbles do not match the app's error
  // styling anyway. Gate submit here instead, mirroring the server's presence rules.
  validateOnSubmit(event) {
    this.#submitAttempted = true;
    if (this.#refreshValidation()) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  // Mirrors the server's presence rules. Returns true when the card has an error. The
  // checks always run so the return value is honest even before the first submit; only
  // the painting is gated on #submitAttempted.
  #refreshValidation() {
    const destinationMissing = this.enabledChecked && !this.#destinationSelected;
    const questionnaireMissing = this.#questionnaireMissing;
    const paint = this.#submitAttempted;

    this.#toggleDestinationError(paint && destinationMissing);
    this.#toggleQuestionnaireError(paint && questionnaireMissing);

    return destinationMissing || questionnaireMissing;
  }

  // A checked-but-disabled radio never reaches the server, so it doesn't count as a
  // chosen destination.
  get #destinationSelected() {
    return this.sourceRadioTargets.some((radio) => radio.checked && !radio.disabled);
  }

  // The select's own `disabled` state already encodes "the questionnaire radio is the
  // active choice" (syncQuestionnaireState maintains it), so switching to Design
  // Packages clears this for free.
  get #questionnaireMissing() {
    return (
      this.enabledChecked &&
      this.hasQuestionnaireSelectTarget &&
      !this.questionnaireSelectTarget.disabled &&
      !this.questionnaireSelectTarget.value
    );
  }

  #toggleDestinationError(visible) {
    if (this.hasDestinationErrorTarget) {
      this.destinationErrorTarget.classList.toggle('hidden', !visible);
    }
    // Only flag the options the user can actually pick — reddening a disabled radio
    // reads as "you chose wrong" when the real blocker is the hint below it.
    this.sourceRadioTargets.forEach((radio) => {
      if (visible && !radio.disabled) {
        radio.setAttribute('aria-invalid', 'true');
      } else {
        radio.removeAttribute('aria-invalid');
      }
    });
  }

  #syncBlocked() {
    this.element.dataset.dirtyFormBlockedValue = this.#overflowing.size > 0;
  }

  #toggleQuestionnaireError(visible) {
    if (!this.hasQuestionnaireSelectTarget) {
      return;
    }
    this.questionnaireSelectTarget.classList.toggle('field--error', visible);
    // setAttribute stringifies the boolean, so this always writes a well-formed
    // "true"/"false"; aria-invalid="false" correctly describes the neutral state.
    this.questionnaireSelectTarget.setAttribute('aria-invalid', visible);
  }

  syncQuestionnaireState() {
    if (!this.hasQuestionnaireSelectTarget) {
      return;
    }

    const questionnaireSelected = this.sourceRadioTargets.some(
      (radio) => radio.checked && radio.value === 'questionnaire'
    );
    this.questionnaireSelectTarget.disabled = !questionnaireSelected;
  }

  markDirty() {
    this.element.dispatchEvent(new Event('change', { bubbles: true }));
  }
}
